import React, { useState, useCallback, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import {
  Grid,
  TextField,
  InputLabel,
  FormControl,
  Snackbar,
  FormHelperText,
  Alert,
  Paper,
  Divider,
  InputAdornment,
  Box,
  CircularProgress,
  Autocomplete,
  Select,
  MenuItem,
  Card,
  CardContent,
  Chip,
  Stack,
} from "@mui/material";
import MDBox from "shared/components/MDBox";
import MDTypography from "shared/components/MDTypography";
import MDButton from "shared/components/MDButton";
import PersonIcon from "@mui/icons-material/Person";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import PrintIcon from "@mui/icons-material/Print";
import DownloadIcon from "@mui/icons-material/Download";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import HistoryIcon from "@mui/icons-material/History";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import WarningIcon from "@mui/icons-material/Warning";
import InfoIcon from "@mui/icons-material/Info";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import PointOfSaleIcon from "@mui/icons-material/PointOfSale";
import ConfirmationNumberIcon from "@mui/icons-material/ConfirmationNumber";

import { useFormCierreTurno } from "../../hooks/useFormCierreTurno";
import ConfirmacionCierreDialog from "./ConfirmacionCierreDialog";

// ==================== COMPONENTES REUTILIZABLES ====================

const StatusBadge = ({ status, text }) => {
  const config = {
    autorizado: { icon: <CheckCircleIcon fontSize="small" />, color: "#27ae60", bg: "#e8f5e9" },
    no_autorizado: { icon: <WarningIcon fontSize="small" />, color: "#e67e22", bg: "#fff3e0" },
    no_encontrado: { icon: <ErrorIcon fontSize="small" />, color: "#e74c3c", bg: "#ffebee" },
    verificando: { icon: <InfoIcon fontSize="small" />, color: "#3498db", bg: "#e3f2fd" },
  };

  const style = config[status] || config.verificando;

  return (
    <Chip
      icon={style.icon}
      label={text}
      size="small"
      sx={{
        bgcolor: style.bg,
        color: style.color,
        fontWeight: "bold",
        "& .MuiChip-icon": { color: style.color },
      }}
    />
  );
};

StatusBadge.propTypes = {
  status: PropTypes.string,
  text: PropTypes.string,
};

const InfoCard = ({ title, value, icon: Icon, color }) => (
  <Paper
    variant="outlined"
    sx={{
      p: 2,
      borderRadius: 2,
      borderColor: `${color}30`,
      bgcolor: `${color}08`,
      height: "100%",
      transition: "all 0.2s",
      "&:hover": { borderColor: color, bgcolor: `${color}12` },
    }}
  >
    <Stack direction="row" alignItems="center" spacing={1.5}>
      <Icon sx={{ color, fontSize: 28 }} />
      <Box>
        <MDTypography variant="caption" color="text.secondary">
          {title}
        </MDTypography>
        <MDTypography variant="h6" fontWeight="bold" sx={{ color }}>
          {value}
        </MDTypography>
      </Box>
    </Stack>
  </Paper>
);

InfoCard.propTypes = {
  title: PropTypes.string,
  value: PropTypes.node,
  icon: PropTypes.elementType,
  color: PropTypes.string,
};

// ==================== FUNCIONES DE FORMATEO DE FECHAS CORREGIDAS ====================
// Estas funciones NO convierten la fecha a la zona horaria local
// Muestran la hora UTC original tal como viene del backend

const formatUTCDateShort = (dateString) => {
  if (!dateString) return "N/A";
  try {
    const date = new Date(dateString);
    const year = date.getUTCFullYear();
    const month = String(date.getUTCMonth() + 1).padStart(2, "0");
    const day = String(date.getUTCDate()).padStart(2, "0");
    return `${day}/${month}/${year}`;
  } catch {
    return dateString;
  }
};

const formatUTCTimeShort = (dateString) => {
  if (!dateString) return "N/A";
  try {
    const date = new Date(dateString);
    let hour = date.getUTCHours();
    const minute = String(date.getUTCMinutes()).padStart(2, "0");
    const ampm = hour >= 12 ? "p.m." : "a.m.";
    hour = hour % 12 || 12;
    return `${hour}:${minute} ${ampm}`;
  } catch {
    return dateString;
  }
};

const formatUTCDateTimeFull = (dateString) => {
  if (!dateString) return "N/A";
  try {
    const date = new Date(dateString);
    const year = date.getUTCFullYear();
    const month = String(date.getUTCMonth() + 1).padStart(2, "0");
    const day = String(date.getUTCDate()).padStart(2, "0");
    let hour = date.getUTCHours();
    const minute = String(date.getUTCMinutes()).padStart(2, "0");
    const second = String(date.getUTCSeconds()).padStart(2, "0");
    const ampm = hour >= 12 ? "p.m." : "a.m.";
    hour = hour % 12 || 12;
    return `${day}/${month}/${year} ${hour}:${minute}:${second} ${ampm}`;
  } catch {
    return dateString;
  }
};

// ==================== COMPONENTE PRINCIPAL ====================

const FormCierreTurno = ({ onSubmit, initialData = {}, loading: externalLoading = false }) => {
  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    handleSubmit,
    handleInputChangeWithFormat,
    handleInputBlurWithFormat,
    getNumericValue,
    parseInputToNumber,
    formatNumberForDisplay,
    isSubmitting,
    submitError,
    submitSuccess,
    setSubmitError,
    setSubmitSuccess,
    backendCierreResult,
    isAdmin,
    isUsuarioIserra,
    isOperador,
    currentUser,
    getCurrentUsername,
    loading: userLoading,
    filteredUsers,
    loadingUsers,
    errorUsers,
    userNotInList,
    userNotFound,
    getSelectedUserName,
    setBackendCierreResult,
    setFieldValue,
    userTurns,
    loadingTurns,
    selectedTurn,
    setSelectedTurn,
    errorTurns,
    autoProcessing,
    pdfGeneratedData,
  } = useFormCierreTurno(onSubmit, initialData);

  const [showPrintDialog, setShowPrintDialog] = useState(false);
  const [formData, setFormData] = useState(null);
  const [turnoCerrado, setTurnoCerrado] = useState(false);
  const [searchInputValue, setSearchInputValue] = useState("");

  const colors = {
    primary: "#2c3e50",
    secondary: "#34495e",
    accent: "#3498db",
    success: "#27ae60",
    warning: "#e67e22",
    error: "#e74c3c",
    info: "#2980b9",
    background: "#f8f9fa",
    border: "#dfe6e9",
    textPrimary: "#2d3436",
    textSecondary: "#636e72",
  };

  const canCloseTurno = useCallback(() => {
    if (isAdmin) return true;
    if (userNotInList || userNotFound) return false;
    if (filteredUsers.length > 0) return true;
    return false;
  }, [isAdmin, userNotInList, userNotFound, filteredUsers.length]);

  const isFormComplete = useCallback(() => {
    if (!canCloseTurno()) return false;
    if (isAdmin && !values.userId) return false;
    if (isAdmin && userTurns.length > 0 && !values.selectedTurnId) return false;

    const efectivoInicialNum = getNumericValue("efectivoInicial");
    const recaudoEfectivoNum = getNumericValue("recaudoEfectivo");

    return (
      !isNaN(efectivoInicialNum) &&
      !isNaN(recaudoEfectivoNum) &&
      efectivoInicialNum >= 0 &&
      recaudoEfectivoNum >= 0
    );
  }, [
    canCloseTurno,
    isAdmin,
    values.userId,
    values.selectedTurnId,
    userTurns.length,
    getNumericValue,
  ]);

  const getUserStatus = useCallback(() => {
    if (userNotInList) return { status: "no_autorizado", text: "NO AUTORIZADO" };
    if (userNotFound) return { status: "no_encontrado", text: "NO ENCONTRADO" };
    if (canCloseTurno()) return { status: "autorizado", text: "AUTORIZADO" };
    return { status: "verificando", text: "VERIFICANDO" };
  }, [userNotInList, userNotFound, canCloseTurno]);

  const handleCloseSnackbar = useCallback(() => {
    setSubmitError(null);
    setSubmitSuccess(false);
  }, [setSubmitError, setSubmitSuccess]);

  const formatCurrency = useCallback(
    (value) => {
      if (value === null || value === undefined) return "$ 0";
      const numValue = parseInputToNumber(value);
      return new Intl.NumberFormat("es-CO", {
        style: "currency",
        currency: "COP",
        minimumFractionDigits: 0,
      }).format(numValue);
    },
    [parseInputToNumber]
  );

  // Efecto para mostrar el diálogo cuando el cierre está completo
  useEffect(() => {
    const hasBalance =
      backendCierreResult?.data?.balance && backendCierreResult.data.balance.length > 0;

    if (hasBalance && !showPrintDialog && !autoProcessing) {
      const backendData = backendCierreResult.data || {};
      const balanceData = backendData.balance?.[0] || {};
      const transData = backendData.trans || {};

      const datosParaDialogo = {
        efectivoInicial: getNumericValue("efectivoInicial"),
        recaudoEfectivo: getNumericValue("recaudoEfectivo"),
        totalRecaudo: getNumericValue("recaudoEfectivo") || 0,
        diferenciaTotal: backendData.diferenciaTotal || 0,
        userId: values.userId,
        usuarioNombre: getSelectedUserName(),
        roleName: isAdmin ? "Administrador" : isUsuarioIserra ? "Usuario ISERRA" : "Operador",
        fechaCierre: new Date().toISOString(),
        backendResult: backendCierreResult,
        efectivoSistema: transData.valorE || 0,
        diferenciaEfectivo: backendData.diferenciaEfectivo || 0,
        turnoId: balanceData.NumeroTurno || "N/A",
        numeroCaja: balanceData.NumeroCaja || "04",
        inicioTurno: balanceData.FHInicio,
        finTurno: balanceData.FHFin,
        usuarioSistema: backendData.user_kme || "Explotador",
        cierreCompletado: true,
        fechaCierreReal: new Date().toISOString(),
        pdfGenerated: true,
        pdfUrl: pdfGeneratedData?.pdfUrl,
        pdfSavedOnServer: pdfGeneratedData?.savedOnServer,
        pdfServerMessage: pdfGeneratedData?.serverMessage,
        pdfFileName: pdfGeneratedData?.fileName,
      };

      setFormData(datosParaDialogo);
      setTurnoCerrado(true);
      setShowPrintDialog(true);
    }
  }, [
    backendCierreResult,
    showPrintDialog,
    autoProcessing,
    values,
    getNumericValue,
    getSelectedUserName,
    isAdmin,
    isUsuarioIserra,
    pdfGeneratedData,
  ]);

  const handleFormSubmit = useCallback(
    async (e) => {
      e.preventDefault();

      if (!canCloseTurno()) {
        setSubmitError(
          userNotInList
            ? "Usuario no autorizado para gestionar cierres de turno"
            : "Usuario no encontrado en la lista de usuarios autorizados"
        );
        return;
      }

      if (isAdmin && !values.userId) {
        setSubmitError("Seleccione un usuario");
        return;
      }

      if (isAdmin && userTurns.length > 0 && !values.selectedTurnId) {
        setSubmitError("Seleccione un turno para cerrar");
        return;
      }

      const efectivoInicialNum = getNumericValue("efectivoInicial");
      const recaudoEfectivoNum = getNumericValue("recaudoEfectivo");

      if (isNaN(efectivoInicialNum) || efectivoInicialNum < 0) {
        setSubmitError(
          efectivoInicialNum < 0
            ? "El efectivo inicial no puede ser negativo"
            : "Ingrese un valor válido para el efectivo inicial"
        );
        return;
      }

      if (isNaN(recaudoEfectivoNum) || recaudoEfectivoNum < 0) {
        setSubmitError(
          recaudoEfectivoNum < 0
            ? "El recaudo en efectivo no puede ser negativo"
            : "Ingrese un valor válido para el recaudo en efectivo"
        );
        return;
      }

      try {
        await handleSubmit();
      } catch (error) {
        setSubmitError(error.message || "Error al procesar el cierre del turno");
      }
    },
    [
      canCloseTurno,
      isAdmin,
      values,
      userNotInList,
      userTurns.length,
      handleSubmit,
      setSubmitError,
      getNumericValue,
    ]
  );

  const handleCancelPrint = useCallback(() => {
    setShowPrintDialog(false);
    setTurnoCerrado(false);
    setFormData(null);
    setBackendCierreResult(null);
  }, [setBackendCierreResult]);

  // ==================== RENDERIZADO DE SECCIONES ====================

  const renderHeader = () => {
    const { status, text } = getUserStatus();
    const statusColor =
      status === "autorizado"
        ? colors.success
        : status === "no_autorizado"
        ? colors.warning
        : status === "no_encontrado"
        ? colors.error
        : colors.primary;

    return (
      <Box
        sx={{
          bgcolor: statusColor,
          color: "white",
          p: 3,
          borderBottom: `1px solid ${colors.border}`,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={2} flexWrap="wrap" gap={2}>
          <AccountBalanceIcon sx={{ fontSize: 40 }} />
          <Box flex={1}>
            <MDTypography variant="h4" fontWeight="bold" color="white">
              Cierre de Turno
            </MDTypography>
            <Stack direction="row" alignItems="center" spacing={2} flexWrap="wrap" sx={{ mt: 0.5 }}>
              <MDTypography variant="body2" color="rgba(255,255,255,0.9)">
                Sistema LINCE V7.0 | Usuario: {getCurrentUsername()}
              </MDTypography>
              <StatusBadge status={status} text={text} />
              <MDTypography variant="caption" color="rgba(255,255,255,0.8)">
                Rol: {isAdmin ? "Administrador" : isUsuarioIserra ? "Usuario ISERRA" : "Operador"}
              </MDTypography>
            </Stack>
          </Box>
        </Stack>
      </Box>
    );
  };

  const renderUserSection = () => {
    if (userNotFound || userNotInList) {
      const { status, text } = getUserStatus();
      const statusColor = status === "no_autorizado" ? colors.warning : colors.error;
      const helperText = userNotInList
        ? `El usuario "${getCurrentUsername()}" no está autorizado`
        : `El usuario "${getCurrentUsername()}" no se encuentra en la lista`;

      return (
        <Card variant="outlined" sx={{ borderColor: statusColor, bgcolor: `${statusColor}08` }}>
          <CardContent>
            <Stack direction="row" alignItems="center" spacing={2}>
              <PersonIcon sx={{ color: statusColor, fontSize: 40 }} />
              <Box flex={1}>
                <MDTypography variant="h6" color={statusColor}>
                  Usuario ({text})
                </MDTypography>
                <MDTypography variant="body1" fontWeight="bold">
                  {getCurrentUsername()}
                </MDTypography>
                <MDTypography variant="caption" color={statusColor}>
                  ⚠️ {helperText}
                </MDTypography>
              </Box>
            </Stack>
          </CardContent>
        </Card>
      );
    }

    if (isAdmin) {
      const userOptions = filteredUsers.map((user) => ({ id: user.id, label: user.username }));
      const selectedUser = values.userId
        ? userOptions.find((opt) => opt.id === Number(values.userId)) || null
        : null;

      return (
        <FormControl fullWidth error={touched.userId && Boolean(errors.userId)}>
          <Autocomplete
            options={userOptions}
            loading={loadingUsers}
            value={selectedUser}
            inputValue={searchInputValue}
            onInputChange={(_, newValue) => setSearchInputValue(newValue)}
            onChange={(_, newValue) => {
              setFieldValue("userId", newValue ? newValue.id.toString() : "");
              setFieldValue("selectedTurnId", "");
              setSelectedTurn(null);
            }}
            isOptionEqualToValue={(option, value) => option.id === value?.id}
            getOptionLabel={(option) => option.label}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Buscar/Seleccionar Usuario *"
                error={touched.userId && Boolean(errors.userId)}
                helperText={touched.userId && errors.userId}
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonIcon sx={{ color: colors.accent }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <>
                      {loadingUsers && <CircularProgress size={20} />}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            disabled={loadingUsers || isSubmitting || externalLoading}
            noOptionsText="No hay usuarios disponibles"
          />
          {errorUsers && !loadingUsers && filteredUsers.length === 0 && (
            <FormHelperText error>{errorUsers}</FormHelperText>
          )}
        </FormControl>
      );
    }

    return (
      <Card variant="outlined" sx={{ borderColor: colors.success, bgcolor: `${colors.success}08` }}>
        <CardContent>
          <Stack direction="row" alignItems="center" spacing={2}>
            <PersonIcon sx={{ color: colors.success, fontSize: 40 }} />
            <Box flex={1}>
              <MDTypography variant="h6" color={colors.success}>
                {isUsuarioIserra ? "Usuario ISERRA" : "Operador"} (AUTORIZADO)
              </MDTypography>
              <MDTypography variant="body1" fontWeight="bold">
                {filteredUsers.length > 0 ? filteredUsers[0]?.username : getCurrentUsername()}
              </MDTypography>
              <MDTypography variant="caption" color={colors.success}>
                ✓ Usuario verificado y autorizado
              </MDTypography>
            </Box>
          </Stack>
        </CardContent>
      </Card>
    );
  };

  const renderTurnSelector = () => {
    if (!isAdmin || userNotFound || userNotInList || !values.userId) return null;

    if (loadingTurns) {
      return (
        <Card variant="outlined" sx={{ borderColor: colors.info, bgcolor: `${colors.info}08` }}>
          <CardContent>
            <Stack direction="row" alignItems="center" spacing={2}>
              <CircularProgress size={24} />
              <MDTypography variant="body2" color={colors.textSecondary}>
                Cargando turnos del usuario...
              </MDTypography>
            </Stack>
          </CardContent>
        </Card>
      );
    }

    if (errorTurns) {
      return (
        <Card variant="outlined" sx={{ borderColor: colors.error, bgcolor: `${colors.error}08` }}>
          <CardContent>
            <Stack direction="row" alignItems="center" spacing={1}>
              <ErrorIcon sx={{ color: colors.error }} />
              <MDTypography variant="body2" color={colors.error}>
                ⚠️ {errorTurns}
              </MDTypography>
            </Stack>
          </CardContent>
        </Card>
      );
    }

    if (userTurns.length === 0) {
      return (
        <Card
          variant="outlined"
          sx={{ borderColor: colors.warning, bgcolor: `${colors.warning}08` }}
        >
          <CardContent>
            <Stack direction="row" alignItems="center" spacing={1}>
              <HistoryIcon sx={{ color: colors.warning }} />
              <MDTypography variant="body2" color={colors.warning}>
                No se encontraron turnos para el usuario seleccionado
              </MDTypography>
            </Stack>
          </CardContent>
        </Card>
      );
    }

    return (
      <Stack spacing={3}>
        <FormControl fullWidth error={touched.selectedTurnId && Boolean(errors.selectedTurnId)}>
          <InputLabel id="turn-select-label" sx={{ backgroundColor: "white", px: 0.5 }}>
            Seleccionar Turno *
          </InputLabel>
          <Select
            labelId="turn-select-label"
            name="selectedTurnId"
            value={values.selectedTurnId || ""}
            onChange={(e) => {
              setFieldValue("selectedTurnId", e.target.value);
              const selected = userTurns.find((turn) => turn.id === Number(e.target.value));
              setSelectedTurn(selected || null);
              handleChange(e);
            }}
            onBlur={handleBlur}
            label="Seleccionar Turno *"
            MenuProps={{
              PaperProps: {
                sx: {
                  maxHeight: 400,
                },
              },
            }}
            sx={{
              height: 45,
              "& .MuiOutlinedInput-input": {
                padding: "12px 14px",
                display: "flex",
                alignItems: "center",
              },
              "& .MuiSelect-select": {
                padding: "12px 14px !important",
                display: "flex",
                alignItems: "center",
                minHeight: "auto",
              },
            }}
            startAdornment={
              <InputAdornment position="start" sx={{ ml: 1 }}>
                <HistoryIcon sx={{ color: colors.accent }} />
              </InputAdornment>
            }
            renderValue={(selected) => {
              const selectedTurnData = userTurns.find((turn) => turn.id === Number(selected));
              if (!selectedTurnData)
                return <MDTypography variant="body2">Seleccione un turno</MDTypography>;
              return (
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1.5}
                  sx={{ width: "100%", flexWrap: "wrap", gap: 1 }}
                >
                  <Chip
                    label={`Caja ${selectedTurnData.numeroCaja}`}
                    size="small"
                    sx={{
                      bgcolor: "#1c9d42ff",
                      color: "white",
                      fontWeight: "bold",
                      height: 28,
                    }}
                  />
                  <Chip
                    label={`Turno #${selectedTurnData.numeroTurno}`}
                    size="small"
                    variant="outlined"
                    sx={{
                      borderColor: colors.info,
                      color: colors.info,
                      fontWeight: "bold",
                      height: 28,
                    }}
                  />
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <CalendarTodayIcon sx={{ fontSize: 14, color: colors.textSecondary }} />
                    <MDTypography variant="caption" color="text.secondary">
                      {formatUTCDateShort(selectedTurnData.fhInicio)}
                    </MDTypography>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <AccessTimeIcon sx={{ fontSize: 14, color: colors.textSecondary }} />
                    <MDTypography variant="caption" color="text.secondary">
                      {formatUTCTimeShort(selectedTurnData.fhInicio)}
                    </MDTypography>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <AccessTimeIcon sx={{ fontSize: 14, color: colors.textSecondary }} />
                    <MDTypography variant="caption" color="text.secondary">
                      → {formatUTCTimeShort(selectedTurnData.fhFin)}
                    </MDTypography>
                  </Box>
                </Stack>
              );
            }}
          >
            {userTurns.map((turn) => (
              <MenuItem key={turn.id} value={turn.id} sx={{ py: 1, px: 2 }}>
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1.5}
                  sx={{ width: "100%", flexWrap: "wrap", gap: 1 }}
                >
                  <Chip
                    label={`Caja ${turn.numeroCaja}`}
                    size="small"
                    sx={{
                      bgcolor: colors.primary,
                      color: "#ffffff",
                      fontWeight: "bold",
                      height: 26,
                    }}
                  />
                  <Chip
                    label={`Turno #${turn.numeroTurno}`}
                    size="small"
                    variant="outlined"
                    sx={{
                      borderColor: colors.info,
                      color: colors.info,
                      fontWeight: "bold",
                      height: 26,
                    }}
                  />
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <CalendarTodayIcon sx={{ fontSize: 12, color: colors.textSecondary }} />
                    <MDTypography variant="caption" color="text.secondary">
                      {formatUTCDateShort(turn.fhInicio)}
                    </MDTypography>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <AccessTimeIcon sx={{ fontSize: 12, color: colors.textSecondary }} />
                    <MDTypography variant="caption" color="text.secondary">
                      {formatUTCTimeShort(turn.fhInicio)}
                    </MDTypography>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <AccessTimeIcon sx={{ fontSize: 12, color: colors.textSecondary }} />
                    <MDTypography variant="caption" color="text.secondary">
                      → {formatUTCTimeShort(turn.fhFin)}
                    </MDTypography>
                  </Box>
                </Stack>
              </MenuItem>
            ))}
          </Select>
          <FormHelperText>
            {touched.selectedTurnId && errors.selectedTurnId}
            {!errors.selectedTurnId && "Seleccione el turno que desea cerrar"}
          </FormHelperText>
        </FormControl>

        {selectedTurn && (
          <Card variant="outlined" sx={{ borderColor: colors.info, bgcolor: `${colors.info}02` }}>
            <CardContent>
              <MDTypography variant="subtitle2" fontWeight="bold" color={colors.info} gutterBottom>
                Información del Turno Seleccionado
              </MDTypography>
              <Divider sx={{ my: 1.5 }} />
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <InfoCard
                    title="Inicio del Turno"
                    value={formatUTCDateTimeFull(selectedTurn.fhInicio)}
                    icon={HistoryIcon}
                    color={colors.info}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <InfoCard
                    title="Fin del Turno"
                    value={formatUTCDateTimeFull(selectedTurn.fhFin)}
                    icon={HistoryIcon}
                    color={colors.info}
                  />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <InfoCard
                    title="Caja"
                    value={selectedTurn.numeroCaja}
                    icon={PointOfSaleIcon}
                    color={colors.primary}
                  />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <InfoCard
                    title="Turno #"
                    value={selectedTurn.numeroTurno}
                    icon={ConfirmationNumberIcon}
                    color={colors.primary}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <InfoCard
                    title="Número de Línea"
                    value={selectedTurn.numeroLinea}
                    icon={InfoIcon}
                    color={colors.info}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        )}
      </Stack>
    );
  };

  const renderFinancialSection = () => (
    <Grid container spacing={3}>
      <Grid item xs={12} md={6}>
        <Paper
          variant="outlined"
          sx={{
            p: 3,
            borderRadius: 2,
            borderColor: canCloseTurno() ? colors.border : colors.error,
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1} mb={2}>
            <AccountBalanceWalletIcon sx={{ color: colors.primary }} />
            <MDTypography variant="subtitle1" fontWeight="medium" color={colors.textPrimary}>
              Efectivo Inicial *
            </MDTypography>
          </Stack>
          <TextField
            type="text"
            name="efectivoInicial"
            value={values.efectivoInicial ?? ""}
            onChange={handleInputChangeWithFormat("efectivoInicial")}
            onBlur={handleInputBlurWithFormat("efectivoInicial")}
            error={touched.efectivoInicial && Boolean(errors.efectivoInicial)}
            helperText={touched.efectivoInicial && errors.efectivoInicial}
            fullWidth
            InputProps={{
              startAdornment: <InputAdornment position="start">$</InputAdornment>,
            }}
            disabled={!canCloseTurno() || isSubmitting || externalLoading}
            placeholder="0"
          />
          {values.efectivoInicial && (
            <MDTypography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
              Valor formateado: {formatCurrency(values.efectivoInicial)}
            </MDTypography>
          )}
          <MDTypography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
            Puede ingresar 0 si no hubo efectivo inicial
          </MDTypography>
        </Paper>
      </Grid>

      <Grid item xs={12} md={6}>
        <Paper
          variant="outlined"
          sx={{
            p: 3,
            borderRadius: 2,
            borderColor: canCloseTurno() ? colors.border : colors.error,
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1} mb={2}>
            <AttachMoneyIcon sx={{ color: colors.info }} />
            <MDTypography variant="subtitle1" fontWeight="medium" color={colors.textPrimary}>
              Recaudo Efectivo *
            </MDTypography>
          </Stack>
          <TextField
            type="text"
            name="recaudoEfectivo"
            value={values.recaudoEfectivo ?? ""}
            onChange={handleInputChangeWithFormat("recaudoEfectivo")}
            onBlur={handleInputBlurWithFormat("recaudoEfectivo")}
            error={touched.recaudoEfectivo && Boolean(errors.recaudoEfectivo)}
            helperText={touched.recaudoEfectivo && errors.recaudoEfectivo}
            fullWidth
            InputProps={{
              startAdornment: <InputAdornment position="start">$</InputAdornment>,
            }}
            disabled={!canCloseTurno() || isSubmitting || externalLoading}
            placeholder="0"
          />
          {values.recaudoEfectivo && (
            <MDTypography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
              Valor formateado: {formatCurrency(values.recaudoEfectivo)}
            </MDTypography>
          )}
          <MDTypography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
            Puede ingresar 0 si no hubo recaudo en efectivo
          </MDTypography>
        </Paper>
      </Grid>
    </Grid>
  );

  const renderSubmitButton = () => {
    const isDisabled =
      !canCloseTurno() ||
      isSubmitting ||
      externalLoading ||
      (isAdmin && !values.userId) ||
      (isAdmin && userTurns.length > 0 && !values.selectedTurnId) ||
      !isFormComplete() ||
      loadingUsers ||
      autoProcessing;
    const buttonColor = isFormComplete()
      ? colors.success
      : canCloseTurno()
      ? colors.primary
      : colors.error;
    const buttonText = !canCloseTurno()
      ? userNotFound
        ? "USUARIO NO ENCONTRADO"
        : "USUARIO NO AUTORIZADO"
      : isSubmitting || autoProcessing
      ? autoProcessing
        ? "Generando comprobante..."
        : "Procesando cierre..."
      : isFormComplete()
      ? "CERRAR TURNO Y VER COMPROBANTE"
      : "COMPLETAR CAMPOS PARA CERRAR TURNO";

    return (
      <MDButton
        type="submit"
        variant="contained"
        fullWidth
        disabled={isDisabled}
        size="large"
        sx={{
          py: 2,
          fontSize: "1rem",
          fontWeight: "bold",
          borderRadius: 2,
          color: "#ffffffff",
          bgcolor: buttonColor,
          "&:hover": {
            bgcolor: isFormComplete()
              ? "#1e8449"
              : buttonColor === colors.primary
              ? colors.secondary
              : buttonColor,
          },
          "&:disabled": { bgcolor: colors.border },
        }}
      >
        {isSubmitting || autoProcessing ? (
          <CircularProgress size={24} sx={{ color: "white" }} />
        ) : (
          <Stack direction="row" alignItems="center" spacing={1}>
            <PrintIcon />
            <span>{buttonText}</span>
          </Stack>
        )}
      </MDButton>
    );
  };

  if (userLoading) {
    return (
      <MDBox display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
        <MDTypography variant="body2" ml={2} color={colors.textSecondary}>
          Cargando información del usuario...
        </MDTypography>
      </MDBox>
    );
  }

  return (
    <>
      <Paper
        elevation={0}
        sx={{ borderRadius: 3, overflow: "hidden", border: `1px solid ${colors.border}`, mb: 3 }}
      >
        {renderHeader()}

        <Box component="form" onSubmit={handleFormSubmit} sx={{ p: 4 }}>
          {/* Sección de Usuario */}
          <Stack spacing={2} mb={4}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <PersonIcon sx={{ color: colors.accent }} />
              <MDTypography variant="h6" fontWeight="medium" color={colors.textPrimary}>
                Responsable del Turno
              </MDTypography>
              {isAdmin && !userNotFound && !userNotInList && (
                <Chip
                  label="Puede buscar escribiendo"
                  size="small"
                  variant="outlined"
                  sx={{ ml: 1 }}
                />
              )}
            </Stack>
            {renderUserSection()}
          </Stack>

          {/* Sección de Turnos (solo admin) */}
          {isAdmin && values.userId && (
            <Stack spacing={2} mb={4}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <HistoryIcon sx={{ color: colors.accent }} />
                <MDTypography variant="h6" fontWeight="medium" color={colors.textPrimary}>
                  Selección de Turno
                </MDTypography>
                <Chip label="Obligatorio" size="small" color="error" />
              </Stack>
              {renderTurnSelector()}
            </Stack>
          )}

          <Divider sx={{ my: 4 }} />

          {/* Sección Financiera */}
          <Stack spacing={2} mb={4}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <AccountBalanceIcon
                sx={{ color: canCloseTurno() ? colors.primary : colors.textSecondary }}
              />
              <MDTypography
                variant="h6"
                fontWeight="medium"
                color={canCloseTurno() ? colors.textPrimary : colors.textSecondary}
              >
                Información Financiera
              </MDTypography>
              {!canCloseTurno() && <Chip label="BLOQUEADO" size="small" color="error" />}
            </Stack>
            {renderFinancialSection()}
          </Stack>

          <Divider sx={{ my: 4 }} />

          {/* Botón de Envío */}
          <Stack spacing={2}>
            {renderSubmitButton()}

            {!canCloseTurno() && (
              <MDTypography variant="caption" color="error" textAlign="center" fontWeight="bold">
                {userNotFound
                  ? `⚠️ El usuario "${getCurrentUsername()}" no se encuentra en la lista de usuarios autorizados.`
                  : `⚠️ El usuario "${getCurrentUsername()}" no está autorizado.`}
              </MDTypography>
            )}

            {canCloseTurno() && !isFormComplete() && (
              <MDTypography variant="caption" color="text.secondary" textAlign="center">
                * Complete todos los campos obligatorios para habilitar el cierre
              </MDTypography>
            )}
          </Stack>
        </Box>
      </Paper>

      <ConfirmacionCierreDialog
        open={showPrintDialog}
        onClose={handleCancelPrint}
        formData={formData}
        responseData={backendCierreResult}
        isAdmin={isAdmin}
        isExplotador={false}
        isOperador={isOperador}
        generatingPDF={false}
        formatCurrency={formatCurrency}
        getSelectedUserName={getSelectedUserName}
        getConfirmationData={() => ({ turnoId: formData?.turnoId || "N/A" })}
        colors={colors}
        turnoCerrado={turnoCerrado}
        pdfGenerated={true}
        onGeneratePDF={() => {}}
        pdfUrl={pdfGeneratedData?.pdfUrl}
        pdfSavedOnServer={pdfGeneratedData?.savedOnServer}
        pdfServerMessage={pdfGeneratedData?.serverMessage}
      />

      {/* Snackbars */}
      <Snackbar
        open={!!submitError}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity="error" onClose={handleCloseSnackbar} sx={{ width: "100%" }}>
          <MDTypography variant="body1" fontWeight="bold">
            Error en el cierre
          </MDTypography>
          <MDTypography variant="body2">{submitError}</MDTypography>
        </Alert>
      </Snackbar>

      <Snackbar
        open={submitSuccess}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity="success" onClose={handleCloseSnackbar} sx={{ width: "100%" }}>
          <MDTypography variant="body1" fontWeight="bold">
            ¡Cierre Completado Exitosamente!
          </MDTypography>
          <MDTypography variant="body2">
            El turno se ha cerrado correctamente.
            {pdfGeneratedData?.savedOnServer &&
              " El comprobante PDF se ha guardado en el servidor y se ha enviado por correo."}
          </MDTypography>
          {pdfGeneratedData?.pdfUrl && (
            <MDButton
              variant="outlined"
              size="small"
              startIcon={<DownloadIcon />}
              onClick={() => {
                const link = document.createElement("a");
                link.href = pdfGeneratedData.pdfUrl;
                link.download =
                  pdfGeneratedData.fileName || `cierre_turno_${formData?.turnoId || "turno"}.pdf`;
                link.click();
              }}
              sx={{ mt: 1 }}
            >
              Descargar Comprobante
            </MDButton>
          )}
        </Alert>
      </Snackbar>
    </>
  );
};

FormCierreTurno.propTypes = {
  onSubmit: PropTypes.func,
  initialData: PropTypes.shape({
    userId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    efectivoInicial: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    recaudoEfectivo: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
  loading: PropTypes.bool,
};

FormCierreTurno.defaultProps = {
  onSubmit: () => {},
  initialData: {},
  loading: false,
};

export default FormCierreTurno;
